import React, { useRef, useEffect } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import './DocumentEditor.css';

interface DocumentEditorProps {
  content: string;
  onContentChange: (content: string) => void;
  isGenerating: boolean;
  generationProgress?: string;
}

const DocumentEditor: React.FC<DocumentEditorProps> = ({ 
  content, 
  onContentChange, 
  isGenerating,
  generationProgress
}) => {
  const quillRef = useRef<ReactQuill>(null);

  const modules = {
    toolbar: [
      [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ 'list': 'ordered'}, { 'list': 'bullet' }],
      [{ 'indent': '-1'}, { 'indent': '+1' }],
      [{ 'color': [] }, { 'background': [] }],
      [{ 'align': [] }],
      ['link', 'image'],
      ['clean']
    ],
  };

  const formats = [
    'header',
    'bold', 'italic', 'underline', 'strike',
    'list', 'bullet',
    'indent',
    'color', 'background',
    'align',
    'link', 'image'
  ];

  const handleChange = (value: string) => {
    onContentChange(value);
  };

  // Auto-scroll to bottom when new content is being generated
  useEffect(() => {
    if (isGenerating && quillRef.current) {
      const quill = quillRef.current.getEditor();
      const editorElement = quill.root;
      if (editorElement) {
        editorElement.scrollTop = editorElement.scrollHeight;
      }
    }
  }, [content, isGenerating]);

  return (
    <div className="document-editor">
      <div className="editor-header">
        <h2>Document Editor</h2>
        {isGenerating && (
          <div className="generating-indicator">
            <span className="generating-text">
              {generationProgress || 'Generating...'}
            </span>
            <div className="generating-dots">
              <span></span>
              <span></span>
              <span></span>
            </div>
          </div>
        )}
      </div>
      
      <div className="editor-container">
        <ReactQuill
          ref={quillRef}
          theme="snow"
          value={content}
          onChange={handleChange}
          modules={modules}
          formats={formats}
          placeholder="Your document will appear here..."
          readOnly={isGenerating}
        />
      </div>
      
      <div className="editor-footer">
        <div className="word-count">
          Words: {content.split(/\s+/).filter(word => word.length > 0).length}
        </div>
        <div className="character-count">
          Characters: {content.length}
        </div>
        {generationProgress && (
          <div className="generation-progress">
            {generationProgress}
          </div>
        )}
      </div>
    </div>
  );
};

export default DocumentEditor; 